import { useRef } from 'react';

export const BLEED = 14;

let _gidc = 0;

// A mirrored Art-Nouveau corner scroll (whiplash volutes) that hugs one corner.
// Drawn in a 0..62 local box anchored at the corner origin; callers flip it with
// a scale() transform so the same geometry serves all four corners. Geometry is
// kept tight to the corner pocket (top sweep stays at y<=22, left leg at x<=22)
// so it never intrudes on the top-left badge zone (SVG x28..164, y28..48).
function CornerScroll({ g, extras }) {
  return (
    <g>
      {/* S-sweep hugging the top edge, curling into an inner volute */}
      <path
        d="M 62 15 C 44 15 30 17 24 22 C 18 27 18 33 23 33 C 28 33 28 27 24 28"
        fill="none" stroke={`url(#${g}chrome)`} strokeWidth={3.2} strokeLinecap="round"
      />
      <path
        d="M 62 15 C 44 15 30 17 24 22 C 18 27 18 33 23 33"
        fill="none" stroke="#ffffff" strokeWidth={0.6} opacity={0.6} strokeLinecap="round"
      />
      {/* leg hugging the left edge, curling into a second volute */}
      <path
        d="M 15 62 C 15 44 17 30 22 24 C 27 18 33 18 33 23 C 33 28 27 28 28 24"
        fill="none" stroke={`url(#${g}chrome)`} strokeWidth={3.2} strokeLinecap="round"
      />
      <path
        d="M 15 62 C 15 44 17 30 22 24"
        fill="none" stroke="#ffffff" strokeWidth={0.6} opacity={0.6} strokeLinecap="round"
      />
      {extras}
    </g>
  );
}

// A 1950s chrome tail-fin with twin round ruby tail-lights. Local box ~40x24,
// callers flip/anchor it into a bottom corner.
function TailFin({ g }) {
  return (
    <g>
      <path d="M 4 6 C 10 6 30 8 40 18 C 26 16 12 16 4 22 Z" fill={`url(#${g}chromerev)`} />
      <circle cx={12} cy={13} r={2.4} fill="#7a0f1e" />
      <circle cx={12} cy={13} r={1.6} fill="#e0405a" />
      <circle cx={11.4} cy={12.4} r={0.5} fill="#ffffff" opacity={0.85} />
      <circle cx={21} cy={14} r={2.4} fill="#7a0f1e" />
      <circle cx={21} cy={14} r={1.6} fill="#e0405a" />
      <circle cx={20.4} cy={13.4} r={0.5} fill="#ffffff" opacity={0.85} />
    </g>
  );
}

// LOWRIDER — an ornate polished-chrome Art-Nouveau scrollwork frame on pure black
// with fine silver pinstriping and a gunmetal inlay. Mirrored whiplash corner
// volutes, a chrome spear hood-ornament crest with a gunmetal inlay top-center,
// chrome tail-fins with twin ruby tail-lights in the bottom corners, a silver
// pinstripe tracing inside the chrome and a symmetric pinstripe filigree across
// the bottom-center. All decoration hugs the border / corners / bleed so the
// centre content stays clear.
export default function LowriderFrame({ w, h, radius = 12, accent }) {
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = `lr${++_gidc}_`;
  const g = idRef.current;

  const W = w + BLEED * 2;
  const H = h + BLEED * 2;
  const x = BLEED;
  const y = BLEED;
  const r = radius + 1;
  const cx = x + w / 2;
  // TL corner gets the pinstripe + sparkle extras; other corners reuse the bare
  // scroll. All extras are kept inside the tight corner pocket (x<26, y<26) so
  // they clear the top-left badge zone (SVG x28..164, y28..48).
  const tlExtras = (
    <g>
      <path d="M 14 26 C 16 18 22 15 26 14" fill="none" stroke="#e3e7ec" strokeWidth={0.9} opacity={0.8} />
      <circle cx={23} cy={23} r={1.6} fill={`url(#${g}chrome)`} />
      <circle cx={24} cy={28} r={1.4} fill={`url(#${g}chrome)`} />
      <path d="M 24 9 L 25 12 L 28 13 L 25 14 L 24 17 L 23 14 L 20 13 L 23 12 Z" fill="#ffffff" opacity={0.85} />
    </g>
  );
  const trExtras = (
    <path d="M 14 26 C 16 18 22 15 26 14" fill="none" stroke="#e3e7ec" strokeWidth={0.9} opacity={0.8} />
  );

  return (
    <div style={{ position: 'absolute', top: -BLEED, left: -BLEED, width: W, height: H, pointerEvents: 'none' }}>
      <svg width={W} height={H} xmlns="http://www.w3.org/2000/svg">
        <defs>
          {/* top-down polished silver chrome */}
          <linearGradient id={`${g}chrome`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#f4f6fa" />
            <stop offset="0.32" stopColor="#cdd3dc" />
            <stop offset="0.58" stopColor="#9aa2b0" />
            <stop offset="1" stopColor="#5c6470" />
          </linearGradient>
          {/* flipped chrome for upside-down (bottom) pieces */}
          <linearGradient id={`${g}chromerev`} x1="0" y1="1" x2="0" y2="0">
            <stop offset="0" stopColor="#f4f6fa" />
            <stop offset="0.32" stopColor="#cdd3dc" />
            <stop offset="0.58" stopColor="#9aa2b0" />
            <stop offset="1" stopColor="#5c6470" />
          </linearGradient>
          {/* dark gunmetal inlay — brushed-metal centre for the spear crest */}
          <linearGradient id={`${g}inlay`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#6b7280" />
            <stop offset="0.5" stopColor="#3a3f47" />
            <stop offset="1" stopColor="#14161a" />
          </linearGradient>
        </defs>

        {/* chrome border ring just inside the card edge */}
        <rect x={x + 1} y={y + 1} width={w - 2} height={h - 2} rx={r} ry={r} fill="none" stroke={`url(#${g}chrome)`} strokeWidth={2.4} />
        <rect x={x + 1} y={y + 1} width={w - 2} height={h - 2} rx={r} ry={r} fill="none" stroke="#ffffff" strokeWidth={0.5} opacity={0.5} />
        {/* silver pinstripe tracing just inside the chrome */}
        <rect x={x + 6} y={y + 6} width={w - 12} height={h - 12} rx={Math.max(2, r - 3)} ry={Math.max(2, r - 3)} fill="none" stroke="#e3e7ec" strokeWidth={0.9} opacity={0.85} />

        {/* corner scrollwork — mirrored into all four corners */}
        <g><CornerScroll g={g} extras={tlExtras} /></g>
        <g transform={`translate(${W},0) scale(-1,1)`}><CornerScroll g={g} extras={trExtras} /></g>
        <g transform={`translate(0,${H}) scale(1,-1)`}><CornerScroll g={g} /></g>
        <g transform={`translate(${W},${H}) scale(-1,-1)`}><CornerScroll g={g} /></g>

        {/* TOP-CENTER chrome spear crest with gunmetal inlay + sparkle */}
        <g transform={`translate(${cx},${y - 6})`}>
          <path d="M -40 8 C -22 4 -12 6 -4 10 C -14 10 -26 12 -40 14 Z" fill={`url(#${g}chrome)`} />
          <path d="M 40 8 C 22 4 12 6 4 10 C 14 10 26 12 40 14 Z" fill={`url(#${g}chrome)`} />
          <path d="M 0 -2 L 14 16 L 0 26 L -14 16 Z" fill={`url(#${g}chrome)`} stroke="#5c6470" strokeWidth={0.5} />
          <path d="M 0 2 L 8 15 L 0 21 L -8 15 Z" fill={`url(#${g}inlay)`} />
          <path d="M 0 -2 L 14 16" fill="none" stroke="#ffffff" strokeWidth={0.7} opacity={0.7} />
          <path d="M 0 -6 L 1.5 -2 L 5 -1 L 1.5 0 L 0 4 L -1.5 0 L -5 -1 L -1.5 -2 Z" fill="#ffffff" opacity={0.9} />
        </g>

        {/* bottom-corner tail-fins with twin ruby lights */}
        <g transform={`translate(${x},${H}) scale(1,-1)`}><TailFin g={g} /></g>
        <g transform={`translate(${W},${H}) scale(-1,-1)`}><TailFin g={g} /></g>

        {/* bottom-center pinstripe filigree */}
        <g transform={`translate(${cx},${y + h + 8})`}>
          <path d="M 0 0 C -10 -6 -22 -6 -30 0 C -24 -3 -14 -3 -8 1" fill="none" stroke="#e3e7ec" strokeWidth={0.9} opacity={0.8} />
          <path d="M 0 0 C 10 -6 22 -6 30 0 C 24 -3 14 -3 8 1" fill="none" stroke="#e3e7ec" strokeWidth={0.9} opacity={0.8} />
        </g>
      </svg>
    </div>
  );
}
